import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useAtomValue } from 'jotai/react'

import { SelectedDate, SelectedDepartures, SelectedRoutes } from '@/states/selection.interface'
import { selectedDateAtom, selectedDeparturesAtom, selectedRoutesAtom } from '@/states/selectionAtom'
import ArrowForwardIcon from '@/ui-cvi/components/dxp-icons/system/ArrowForwardIcon'

import { departureLegDateFormat } from '@/utils'
import { getArrivalRouteTitle, getDepartureRouteTitle } from '@/utils/routeHelpers'

import { RouteFilter } from '../(departure-filters)/RouteFilters'

interface HeaderProps {
  editElement?: ReactElement
  isOpen: boolean
  returnLeg?: boolean
  selectedRouteFilters: RouteFilter
}

export const CompoundRouteHeader = ({ editElement, returnLeg, selectedRouteFilters }: HeaderProps) => {
  const selectedDate: SelectedDate = useAtomValue(selectedDateAtom)
  const selectedRoutes: SelectedRoutes = useAtomValue(selectedRoutesAtom)
  const selectedDepartures: SelectedDepartures = useAtomValue(selectedDeparturesAtom)
  const { t } = useTranslation()

  return (
    <div className="gap-semantic-global-2xs flex flex-col">
      {returnLeg ? (
        <div className="mt-semantic-global-2xl flex items-center justify-center" id="returnJourneyPrompt">
          <div className="relative flex w-full items-center">
            <div className="border-semantic-global-border-light absolute inset-x-0 border-t" />
            <span className="px-semantic-global-lg py-semantic-global-xs bg-semantic-global-border-lighter typography-body-sm-regular text-semantic-global-text-default relative mx-auto">
              {t('SELECT-RETURN-JOURNEY')}
            </span>
          </div>
        </div>
      ) : null}
      {/* min height keeps the row stable when the edit button mounts/unmounts */}
      <div className="flex min-h-10.5 flex-row items-center">
        <label className="typography-body-lg-medium lg:typography-heading-md text-semantic-global-background-deep-blue">
          {returnLeg ? t('RETURN') : t('OUTBOUND')}
        </label>
        {editElement ? <div className="ml-auto">{editElement}</div> : null}
      </div>
      <div className="typography-body-sm-medium lg:typography-body-md-regular text-semantic-global-text-light flex flex-row items-center">
        <span>
          {returnLeg
            ? selectedDate?.returnDepartureDate && departureLegDateFormat(selectedDate.returnDepartureDate)
            : selectedDate?.outboundDepartureDate && departureLegDateFormat(selectedDate.outboundDepartureDate)}
        </span>
        <span className="bg-semantic-global-text-light mx-semantic-global-2xs inline-block h-[5px] w-[5px] rounded-full" />
        {returnLeg ? (
          <div className="flex flex-row items-center">
            {getDepartureRouteTitle(true, selectedRouteFilters, selectedRoutes, selectedDepartures, t)}
            <ArrowForwardIcon className="mx-semantic-global-2xs" size="md" />
            {getArrivalRouteTitle(true, selectedRouteFilters, selectedRoutes, selectedDepartures, t)}
          </div>
        ) : (
          <div className="flex flex-row items-center">
            {getDepartureRouteTitle(false, selectedRouteFilters, selectedRoutes, selectedDepartures, t)}
            <ArrowForwardIcon className="mx-semantic-global-2xs" size="md" />
            {getArrivalRouteTitle(false, selectedRouteFilters, selectedRoutes, selectedDepartures, t)}
          </div>
        )}
      </div>
      {returnLeg && !selectedDepartures.departureOut ? (
        <div className="gap-semantic-global-sm p-semantic-global-lg mt-semantic-global-md bg-semantic-global-disabled-light flex flex-col items-center justify-center">
          <div className="typography-body-md-medium text-semantic-global-disabled-darker text-center">
            {t('SELECT-OUTBOUND-DEPARTURE-FIRST')}
          </div>
          <div className="typography-body-md-regular text-semantic-global-disabled-darker text-center">
            {t('CHOOSE-OUTBOUND-JOURNEY-FIRST')}
          </div>
        </div>
      ) : null}
    </div>
  )
}
